/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: sms/pages/SmsRoutesPage.tsx
 🗓️ Created: 2026-01-20
 🧩 Description:
    SMS Routes management page - following Extensions module pattern.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Route, Plus, Edit, Trash2, CheckCircle2, XCircle } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { ROUTES } from '../../../constants/routes';
import DataGridSimple from '../../../components/ui/DataGridSimple';
import { useNotifications } from '../../../contexts/NotificationContext';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { fetchRoutes, deleteRoute } from '../api';
import type { SmsRoute } from '../types';

interface RouteColumn {
    key: string;
    label: string;
    sortable: boolean;
    visible: boolean;
    render?: (value: unknown, item: SmsRoute) => React.ReactNode;
}

export default function SmsRoutesPage() {
    const { t, i18n } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const navigate = useNavigate();
    const [data, setData] = useState<SmsRoute[]>([]);
    const [loading, setLoading] = useState(true);
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; route: SmsRoute | null }>({
        isOpen: false,
        route: null,
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('priority');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    // Define columns
    const availableColumns = useMemo<RouteColumn[]>(() => [
        {
            key: 'name',
            label: t('sms.routes.columns.name'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsRoute) => (
                <span className="font-medium text-primary">{item?.name || '-'}</span>
            ),
        },
        {
            key: 'prefixPattern',
            label: t('sms.routes.columns.pattern'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsRoute) => (
                <span className="font-mono text-sm">
                    {item?.prefixPattern || item?.countryCode || '*'}
                </span>
            ),
        },
        {
            key: 'provider',
            label: t('sms.routes.columns.provider'),
            sortable: false,
            visible: true,
            render: (_value: unknown, item: SmsRoute) => (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400">
                    {item?.provider?.name || '-'}
                </span>
            ),
        },
        {
            key: 'priority',
            label: t('sms.routes.columns.priority'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsRoute) => item?.priority || '-',
        },
        {
            key: 'enabled',
            label: t('sms.routes.columns.enabled'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsRoute) => (
                item?.enabled ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                )
            ),
        },
        {
            key: 'actions',
            label: t('common.actions'),
            sortable: false,
            visible: true,
        },
    ], [t, i18n.language]);

    const [columns, setColumns] = useState<RouteColumn[]>(availableColumns);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const routes = await fetchRoutes();
            setData(routes);
        } catch (error) {
            showError(error instanceof Error ? error.message : t('sms.routes.errorLoading'));
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [showError, t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Filter and sort data
    const filteredData = useMemo(() => {
        let result = [...data];

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(item =>
                item.name.toLowerCase().includes(query) ||
                (item.prefixPattern || '').toLowerCase().includes(query)
            );
        }

        result.sort((a, b) => {
            const aVal = (a as unknown as Record<string, unknown>)[sortBy] ?? '';
            const bVal = (b as unknown as Record<string, unknown>)[sortBy] ?? '';

            if (typeof aVal === 'string' && typeof bVal === 'string') {
                const cmp = aVal.localeCompare(bVal);
                return sortOrder === 'asc' ? cmp : -cmp;
            }

            const aNum = typeof aVal === 'number' ? aVal : 0;
            const bNum = typeof bVal === 'number' ? bVal : 0;
            return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
        });

        return result;
    }, [data, searchQuery, sortBy, sortOrder]);

    const paginatedData = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredData.slice(start, start + limit);
    }, [filteredData, page, limit]);

    const pagination = useMemo(() => ({
        page,
        limit,
        total: filteredData.length,
        totalPages: Math.ceil(filteredData.length / limit),
    }), [page, limit, filteredData.length]);

    const handleSearch = useCallback((query: string) => {
        setSearchQuery(query);
        setPage(1);
    }, []);

    const handleSort = useCallback((key: string) => {
        if (sortBy === key) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortOrder('asc');
        }
    }, [sortBy]);

    const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
    const handleItemsPerPageChange = useCallback((newLimit: number) => {
        setLimit(newLimit);
        setPage(1);
    }, []);

    const handleColumnToggle = useCallback((key: string) => {
        setColumns(prev => prev.map(col =>
            col.key === key ? { ...col, visible: !col.visible } : col
        ));
    }, []);

    const handleCreate = useCallback(() => {
        navigate(ROUTES.SMS_ROUTES_NEW);
    }, [navigate]);

    const handleEdit = useCallback((route: SmsRoute) => {
        const idToUse = route.uuid ?? route.id;
        navigate(ROUTES.SMS_ROUTE_DETAIL(idToUse));
    }, [navigate]);

    const handleDelete = useCallback((route: SmsRoute) => {
        setDeleteModal({ isOpen: true, route });
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!deleteModal.route) return;

        try {
            const idToUse = deleteModal.route.uuid ?? deleteModal.route.id;
            await deleteRoute(idToUse);
            showSuccess(t('sms.routes.messages.deleteSuccess'));
            setDeleteModal({ isOpen: false, route: null });
            loadData();
        } catch (error) {
            showError(error instanceof Error ? error.message : t('sms.routes.messages.deleteError'));
        }
    }, [deleteModal.route, loadData, showError, showSuccess, t]);

    const renderActions = useCallback((route: SmsRoute) => (
        <div className="flex items-center gap-2">
            <SimpleTooltip content={t('common.edit')} position="top">
                <button
                    onClick={() => handleEdit(route)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={t('common.delete')} position="top">
                <button
                    onClick={() => handleDelete(route)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                </button>
            </SimpleTooltip>
        </div>
    ), [t, handleEdit, handleDelete]);

    return (
        <div className="p-6">
            <DataGridSimple
                data={paginatedData}
                loading={loading}
                columns={columns}
                pagination={pagination}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSearch={handleSearch}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                onColumnToggle={handleColumnToggle}
                renderActions={renderActions}
                searchPlaceholder={t('sms.routes.searchPlaceholder')}
                title={t('sms.routes.title')}
                titleIcon={<Route className="h-5 w-5 text-primary" />}
                actions={
                    <div className="flex items-center gap-2">
                        <SimpleTooltip content={t('sms.routes.actions.add')} position="top">
                            <button
                                onClick={handleCreate}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
                                <Plus className="h-4 w-4" /> {t('sms.routes.actions.add')}
                            </button>
                        </SimpleTooltip>
                    </div>
                }
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, route: null })}
                onConfirm={confirmDelete}
                title={t('sms.routes.deleteTitle')}
                message={`${t('sms.routes.messages.confirmDelete')} (${deleteModal.route?.name})`}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                type="danger"
            />
        </div>
    );
}
